import { Input, Select, Space, Tag, TreeSelect } from "antd";
import { type TreeSelectProps } from "antd/lib/tree-select";
import {
    createRef,
    type FocusEventHandler,
    Fragment,
    PureComponent,
    type ReactElement,
    type ReactNode,
} from "react";

import { IconCaretSmallDown, IconCheck } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { getAllIds, getByValue, getDisabledIds } from "utilities/form/form";
import { addCloseOnScroll, getClosestModal, removeCloseOnScroll } from "utilities/helpers";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTIconCaretSmallDown } from "commonComponents/btWrappers/BTIcon";
import { BTSelectGroupHeaderId } from "commonComponents/btWrappers/BTSelect/BTSelect.types";
import {
    BTSelectCustomItemContext,
    type IBTSelectCustomItemContext,
} from "commonComponents/btWrappers/BTSelect/BTSelectCustomItemContext";
import ReadMore from "commonComponents/utilities/ReadMore/ReadMore";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import "./BTSelect.bds.less";
import "./BTSelect.less";

const { Option, OptGroup } = Select;
const { TreeNode } = TreeSelect;

// note set in less as well
const STANDARD_OPTION_HEIGHT = 32;

export const BTSelectCheckAllItems = "~~~BTSelectCheckAllItems~~~";

export type BTSelectMaxHeight = "xsmall" | "small" | "middle" | "large";

interface IAntSelectNode {
    value?: string | number;
    title?: React.ReactNode;
    disabled?: boolean;
    "data-searchvalue"?: string;
}

interface IAntTreeSelectNode {
    value?: string | number;
    title?: React.ReactNode;
    disabled?: boolean;
    isLeaf?: boolean;
    disabledCheckbox?: boolean;
    searchValue?: string;
    children?: IAntTreeSelectNode[];
}

export interface IBTSelectProps<FormValues, ExtraDataType> {
    ariaLabel?: string;
    id: KeyOfOrString<FormValues>;

    "data-testid": string;
    readOnly?: boolean;

    /** set to true to make this a multi-select, defaults to false */
    multiple?: boolean;

    className?: string;

    /**
     * Note: When using this prop must specify the addon width and the BTSelect width
     * @example <BTSelect style={{ width: "calc(100% - 100px)" }} addonBefore={ <BTButton style={{ width: 100 }}>Click Me!</BTButton }
     */
    addonBefore?: React.ReactElement<any> | React.ReactElement<any>[];
    /**
     * Note: When using this prop must specify the addon width and the BTSelect width
     * @example <BTSelect style={{ width: "calc(100% - 100px)" }} addonAfter={ <BTButton style={{ width: 100 }}>Click Me!</BTButton }
     */
    addonAfter?: ReactNode | ReactNode[];

    children?: ReactNode;

    /** Pass setFieldValue or a custom function (if you want to use extra data) -- DO NOT PASS handleChange.
     * When using extra data, it is assumed that the render function is pure and unchanging. If extra data is updated, it may not render again.
     */
    onChange: (field: string, value: any, selectedItem?: BTSelectItem<ExtraDataType>) => void;

    /** Pass setFieldTouched -- DO NOT PASS handleBlur. Additionally, please note that this method will not be triggered when the BTSelect is focused & unfocused by removing nodes via the 'X' icon. */
    onBlur: (field: string, isTouched: boolean) => void;

    /** Called when the select is focused */
    onFocus?: FocusEventHandler<HTMLElement>;

    /** Called when the select loses focus (native blur event) */
    onNativeBlur?: () => void;

    treeData?: BTSelectItem<ExtraDataType>[];

    /**
     * Set this if default item has a different value than the default
     * Only needed if single select and in readonly mode
     * @default "-1"
     */
    ungroupedIds?: string[];

    /**
     * Set this if you want to display the default BTSelectItem title if selected in readonly mode
     * @default false
     */
    displayDefaultItemReadonly?: boolean;

    /**
     * Custom placeholder text appears when treeData is empty []
     * @default "There are no items to select"
     */
    noTreeDataPlaceholder?: ReactNode;

    /**
     * Custom placeholder text appears when user does not have view permissions
     *
     * This is for BTSelectEditable only
     */
    notVisiblePlaceholder?: ReactNode;

    /**
     * Enables disabling values via formik state, rather than off of initial load of tree data
     */
    disabledIds?: (number | string)[];

    /**
     * When using customItemRender, it is assumed that the function passed is pure. Additional renders will not occur unless the select items are changed.
     * Do NOT reference props or state directly in this render function.
     * Deps should be passed via extraData on the select items themselves.
     * @default (item) => item.title
     */
    customItemRender?: (item: BTSelectItem<ExtraDataType>) => ReactNode;

    /** Ref for the select */
    fieldRef?: React.RefObject<any>;

    /**
     * max-height of the select
     * xsmall = 1 standard row (32px)
     * small = 2 standard row (64px)
     * middle = 3 standard rows (96px)
     * large = 4 standard rows (128px)
     *
     * Should generally not be used
     * @default middle
     */
    maxHeight?: BTSelectMaxHeight;

    /** Determines whether to hide the Select All item */
    hideSelectAll?: boolean;

    /** Changes multiselect to have single tag displaying "All Selected" when all items are checked
     * @default false
     */
    filterMode?: boolean;

    /**
     * Only works when "multiple" is false.
     * When an item is selected, display a different label by specifying
     * the name of the property that contains the label that you want to display.
     */
    optionLabelProp?: KeyOfOrString<BTSelectItem<ExtraDataType>>;

    /**
     * Function that returns the BTSelectItem value to filter on.
     * @default (item) => item.title
     * @example (item) => item.extraData.myCustomFilterValue
     */
    filterOn?: (item: BTSelectItem<ExtraDataType>) => string;

    /**
     * This controls whether the filtering happens internally in this component or externally.  Set
     * it false when treeData state will be filtered externally by the consuming component.
     * @default true
     */
    shouldFilterOptions?: boolean;

    /**
     * This controls whether the select dropdown items will shrink and expand when longer items
     * scroll into view. Set it to false to make the dropdown width match the select input width.
     * @default true
     */
    dropdownCanExpand?: boolean;

    /**
     * This controls whether the select dropdown will open by default on component mount.
     * @default false
     */
    defaultOpen?: boolean;

    /**
     * When searching will only match on child nodes
     * @default true
     */
    onlySearchChildNodes?: boolean;

    keepEnabledWhenEmpty?: boolean;

    /**
     * By default, virtual scrolling is enabled, which doesn't support dynamic dropdown width.
     * Setting this prop will opt-out of virtual scrolling to enable dynamic width dropdowns.
     * @default false
     */
    disableVirtualScroll?: boolean;

    /**
     * When true, hides the checkbox for items that have disableIndividualSelection set.
     * @default false
     */
    removeDisabledCheckboxes?: boolean;

    /**
     * Controls the form interaction mode. When set to "click-to-edit", the component appears
     * like a read-only field with transparent borders, becoming editable on hover/focus.
     * @default "always-editing"
     */
    formMode?: "always-editing" | "click-to-edit";

    /**
     * Initial selected value(s) for the uncontrolled case. Ignored when `value` is
     * provided (controlled wins). Use a single value for single-select and an array
     * for multi-select.
     */
    defaultValue?: string | number | (string | number)[];

    /** When true, shows a loading spinner and makes the control non-interactive. */
    loading?: boolean;

    /**
     * When true, renders the modern BDS look (pill tags, blue check icon,
     * token-based styling) instead of the legacy antd look. This is the
     * consolidation path for former `BdsSelect` consumers — same antd DOM and
     * behavior, re-skinned via `BTSelect.bds.less`.
     * @default false
     */
    bds?: boolean;
}

type TreeSelectPropsSubset = Omit<
    TreeSelectProps<any>,
    | "id"
    | "treeData"
    | "onChange"
    | "onBlur"
    | "multiple"
    | "treeCheckable"
    | "open"
    | "onDropdownVisibleChange"
    // Narrowed on IBTSelectProps to `string | number` instead of antd's broad ValueType
    | "defaultValue"
>;

export type BTSelectProps<FormValues, ExtraDataType> = IBTSelectProps<FormValues, ExtraDataType> &
    TreeSelectPropsSubset;

export const btSelectDefaultValue = -1;

type InternalTreeSelectProps = TreeSelectProps<(string | number)[]>;

/** DO NOT USE - These props are only meant for internal wrappers of this component */
interface IBTSelectInternalProps<FormValues, ExtraDataType>
    extends BTSelectProps<FormValues, ExtraDataType> {
    treeCheckable: TreeSelectProps<any>["treeCheckable"];
}

interface IBTSelectInternalState {
    open: boolean;
    searchValue: string;
    controlHeld: boolean;
}

function defaultCustomItemRender(item: BTSelectItem<any>) {
    return item.title;
}

class BTSelectInternalBase<FormValues = undefined, ExtraDataType = undefined> extends PureComponent<
    IBTSelectInternalProps<FormValues, ExtraDataType> & ITrackingProp,
    IBTSelectInternalState
> {
    static defaultProps = {
        multiple: false,
        ungroupedIds: [`${btSelectDefaultValue}`],
        displayDefaultItemReadonly: false,
        noTreeDataPlaceholder: "There are no items to select",
        customItemRender: defaultCustomItemRender,
        treeCheckable: true,
        filterMode: false,
        getPopupContainer: getClosestModal,
        filterOn: (item: BTSelectItem<any>) => item.title,
        shouldFilterOptions: true,
        dropdownCanExpand: false,
        maxHeight: "middle",
        onlySearchChildNodes: true,
        disableVirtualScroll: false,
    };

    // Initialize `open` from `defaultOpen` so the controlled `open` prop is true
    // on the very first render. rc-select renders the search input as readOnly
    // when the dropdown is closed and unfocused; deferring open=true to
    // componentDidMount caused React's autoFocus to fire against a readOnly
    // input, which iOS Safari refuses to show a keyboard for and which trips
    // ag-grid's `stopEditingWhenCellsLoseFocus` if anything tries to recover
    // it via blur+refocus. Starting open=true lets the input render editable
    // from the first commit so the autoFocus lands on an editable element.
    state: Readonly<IBTSelectInternalState> = {
        open: !!this.props.defaultOpen,
        searchValue: "",
        controlHeld: false,
    };

    remountSelect = 0;
    canRemountSelect = false;
    inputGroup = createRef<HTMLDivElement>();
    btSelectCustomItemContextInputValue: IBTSelectCustomItemContext = {
        isSelectedValueRender: true,
    };
    btSelectCustomItemContextDropdownValue: IBTSelectCustomItemContext = {
        isSelectedValueRender: false,
    };

    private controlDownHandler = (e: KeyboardEvent) => {
        if (e.key === "Control") {
            this.setState({ controlHeld: true });
        }
    };

    private controlUpHandler = (e: KeyboardEvent) => {
        if (e.key === "Control") {
            this.setState({ controlHeld: false });
        }
    };

    componentDidMount() {
        window.addEventListener("keydown", this.controlDownHandler);
        window.addEventListener("keyup", this.controlUpHandler);
        // onDropdownVisibleChange doesn't fire for the initial controlled
        // open=true, so bind the close-on-scroll listener directly when we
        // start open.
        if (this.state.open && this.inputGroup.current) {
            addCloseOnScroll(this.inputGroup.current, this.handleScroll);
        }
        // iOS Safari refuses the on-screen keyboard when focus() lands on a
        // readOnly input. rc-select v12 (under antd v4) renders the search
        // input as readOnly while not (open || focused), and the React
        // re-render from a tap doesn't flush before rc-select's onClick
        // fires .focus(). Clear readOnly synchronously inside the tap so
        // focus lands on an editable input. Upstream rc-select dropped the
        // readOnly attribute entirely — delete this and its companion test
        // (`should keep the input editable at every focus() call during a
        // user tap`) on the antd v5/v6 upgrade. Refs: ant-design#26648,
        // NG-ZORRO/ng-zorro-antd#5563.
        this.inputGroup.current?.addEventListener(
            "touchstart",
            this.handleTouchStartClearReadOnly,
            { capture: true, passive: true },
        );
    }

    componentDidUpdate(prevProps: BTSelectProps<FormValues, ExtraDataType>) {
        if (
            this.props.multiple &&
            this.canRemountSelect &&
            prevProps.treeData &&
            this.props.treeData &&
            prevProps.treeData !== this.props.treeData
        ) {
            // If the treeData reference changes, we want to reset the virtual scroll position back to the beginning by remounting the component
            this.remountSelect++;
        }
    }

    componentWillUnmount() {
        window.removeEventListener("keydown", this.controlDownHandler);
        window.removeEventListener("keyup", this.controlUpHandler);
        this.inputGroup.current?.removeEventListener(
            "touchstart",
            this.handleTouchStartClearReadOnly,
            { capture: true },
        );
        this.handleDropdownVisibleChange(false);
    }

    private handleTouchStartClearReadOnly = () => {
        const input = this.inputGroup.current?.querySelector<HTMLInputElement>(
            ".ant-select-show-search .ant-select-selection-search-input",
        );
        if (input?.readOnly) {
            input.readOnly = false;
        }
    };

    /** Do not use directly */
    private _btSelectItems: {
        btSelectItems: BTSelectItem<ExtraDataType>[];
        rawPropValues: Pick<
            IBTSelectInternalProps<FormValues, ExtraDataType>,
            "treeData" | "multiple" | "disabledIds" | "treeCheckable" | "value" | "hideSelectAll"
        >;
    };
    private get btSelectItems() {
        const { treeData, multiple, disabledIds, treeCheckable, value, hideSelectAll, filterMode } =
            this.props;
        if (
            !this._btSelectItems ||
            this._btSelectItems.rawPropValues.treeData !== treeData ||
            this._btSelectItems.rawPropValues.multiple !== multiple ||
            this._btSelectItems.rawPropValues.disabledIds !== disabledIds ||
            this._btSelectItems.rawPropValues.treeCheckable !== treeCheckable ||
            this._btSelectItems.rawPropValues.value !== value ||
            this._btSelectItems.rawPropValues.hideSelectAll !== hideSelectAll
        ) {
            const actualDisabledIds = disabledIds ?? getDisabledIds(treeData);
            let btSelectItems = this.mapInternalSelectItemsFromTreeData(
                treeData ?? [],
                actualDisabledIds,
                value,
            );
            if (multiple) {
                let allOptionText = filterMode ? "Select All" : "Check All";
                if (!treeCheckable) {
                    allOptionText =
                        Array.isArray(value) && value.length > 0 ? "Clear All" : "Select All";
                }
                if (!hideSelectAll) {
                    // Add check all parent
                    btSelectItems = [
                        new BTSelectItem({
                            id: BTSelectCheckAllItems,
                            title: allOptionText,
                            name: allOptionText,
                            value: BTSelectCheckAllItems,
                            children: btSelectItems,
                            className: "BTSelectCheckAll",
                        }),
                    ];
                }
            }

            this._btSelectItems = {
                btSelectItems,
                rawPropValues: { treeData, multiple, disabledIds, treeCheckable, value },
            };
        }
        return this._btSelectItems.btSelectItems;
    }
    _renderedSelectItems: {
        renderedSelectItems: ReactNode[];
        deps: { multiple: boolean | undefined; btSelectItems: BTSelectItem<ExtraDataType>[] };
    };
    private get renderedSelectItems() {
        const { multiple } = this.props;
        const btSelectItems = this.btSelectItems;
        if (
            !this._renderedSelectItems ||
            this._renderedSelectItems.deps.multiple !== multiple ||
            this._renderedSelectItems.deps.btSelectItems !== btSelectItems
        ) {
            const renderedSelectItems = multiple
                ? this.btSelectItems.map((item) => this.renderTreeNode(item))
                : this.btSelectItems.map(this.renderGroup);
            this._renderedSelectItems = {
                renderedSelectItems,
                deps: { multiple, btSelectItems },
            };
        }
        return this._renderedSelectItems.renderedSelectItems;
    }

    private handleSearch = (value: string) => {
        const { onSearch } = this.props;
        this.setState({ searchValue: value });
        onSearch?.(value);
    };

    private handleSelectFilterMode(value: (string | number)[], wasNewValueAdded: boolean): any[] {
        const checkAll = this.btSelectItems.find((x) => x.id === BTSelectCheckAllItems);
        let newValues = value;
        if (newValues.includes(BTSelectCheckAllItems)) {
            newValues.pop();
            if (checkAll?.children && wasNewValueAdded) {
                checkAll.children.forEach((child) => {
                    child.children
                        ? child.children.forEach((c) => {
                              newValues.push(c.id);
                          })
                        : newValues.push(child.id);
                });
            }
        } else if (newValues.some((item) => item.toString().includes(BTSelectGroupHeaderId))) {
            const parentIds = newValues.filter((item) =>
                item.toString().includes(BTSelectGroupHeaderId),
            );
            parentIds.forEach((parentId) => {
                const parent = checkAll?.children?.find((child) => child.id === parentId);
                if (parent?.children) {
                    // Remove the header item from the list
                    newValues = newValues.filter((item) => !item.toString().includes(parent.id));
                    // Add children for the found parent
                    parent.children.forEach((child) => {
                        newValues.push(child.id);
                    });
                }
            });
        }
        return newValues;
    }

    private handleSingleSelectChange = (value: string | number) => {
        const selectedItem = getByValue(this.props.treeData, value);
        this.props.onChange(this.props.id as string, value, selectedItem);
        this.props.tracking?.trackEvent({
            event: "ValueChange",
            value,
            extraInfo: {
                title: selectedItem?.title,
                value,
            },
        });
    };

    private findMatchingItem(
        selectItems: BTSelectItem<ExtraDataType>[],
        selectedValue: string | number,
    ): BTSelectItem<ExtraDataType> | undefined {
        for (const item of selectItems) {
            if (item.value === selectedValue) {
                return item;
            } else if (item.children) {
                const match = this.findMatchingItem(item.children, selectedValue);
                if (match) {
                    return match;
                }
            }
        }

        return undefined;
    }

    private handleMultipleSelectChange: InternalTreeSelectProps["onChange"] = (
        value,
        _label,
        { triggerValue, checked, preValue, selected },
    ) => {
        const { searchValue, controlHeld } = this.state;

        if (!controlHeld) {
            this.setState({ searchValue: "" });
        }

        let newValue = value;

        // Check if the trigger item has individual selection disabled
        const triggerItem = this.findMatchingItem(this.btSelectItems, triggerValue);
        if (triggerItem?.disableIndividualSelection) {
            // Prevent direct selection/deselection of items with disabled individual selection - return early without onChange
            return;
        }

        const isFiltered = this.props.shouldFilterOptions && !!searchValue;
        // handles both cases of `treeCheckable`
        const wasNewValueAdded = checked ?? selected ?? false;
        if (isFiltered && wasNewValueAdded && Array.isArray(newValue)) {
            const completingGroup = newValue.some(
                (item) =>
                    item.toString().includes(BTSelectGroupHeaderId) &&
                    this.findMatchingItem(this.btSelectItems, item)
                        ?.children?.map((child) => child.value)
                        .indexOf(triggerValue) !== -1,
            );

            if (triggerItem && !completingGroup) {
                const previouslySelected = newValue.filter((v) =>
                    preValue.some((pv) => pv.value === v),
                );

                if (this.includesItemWithDisabledIndividualSelection(triggerItem)) {
                    // Select all items within a group if at least one child has disableIndividualSelection enabled
                    const filteredDisplayedValues = this.getAllItemsForCheckAll(
                        triggerItem,
                        searchValue,
                    );
                    newValue = [...previouslySelected, ...filteredDisplayedValues];
                } else {
                    // Default: only select filtered/shown children
                    newValue = [
                        ...previouslySelected,
                        ...this.filterToOnlyDisplayedChildren(triggerItem, searchValue),
                    ];
                }
            }
        }

        if (this.props.filterMode) {
            newValue = this.handleSelectFilterMode(newValue, wasNewValueAdded);
        }

        const selectedItem = getByValue(this.props.treeData, triggerValue);
        this.props.onChange(this.props.id as string, newValue, selectedItem);

        this.props.tracking?.trackEvent({
            event: "ValueChange",
            value: selectedItem?.value as string | number | undefined,
            extraInfo: {
                title: selectedItem?.title,
                value: triggerValue,
                userSelected: wasNewValueAdded,
                totalSelected: newValue.length,
            },
        });
    };

    private filterToOnlyDisplayedChildren(
        triggerItem: BTSelectItem<ExtraDataType>,
        searchValue: string,
    ): (string | number)[] {
        const { children } = triggerItem;
        const isLeaf = !triggerItem.children || triggerItem.children.length === 0;

        if (!isLeaf) {
            return (children ?? []).flatMap((c) =>
                this.filterToOnlyDisplayedChildren(c, searchValue),
            );
        }
        if (
            !triggerItem.disabled &&
            // if we're including parent nodes in the search results then we will also want to
            // be able to select a parent node and all its children. Use case: bidding subs by divisions
            (!this.props.onlySearchChildNodes ||
                this.props.filterOn!(triggerItem).toLowerCase().includes(searchValue.toLowerCase()))
        ) {
            return [triggerItem.value];
        }
        return [];
    }

    private includesItemWithDisabledIndividualSelection(
        item: BTSelectItem<ExtraDataType>,
    ): boolean {
        if (!item.children || item.children.length === 0) {
            return !!item.disableIndividualSelection;
        }
        return item.children.some((child) =>
            this.includesItemWithDisabledIndividualSelection(child),
        );
    }

    private getAllLeafChildValues = (item: BTSelectItem<ExtraDataType>): (string | number)[] => {
        if (!item.children || item.children.length === 0) {
            return !item.disabled ? [item.value as string | number] : [];
        }
        return item.children.flatMap(this.getAllLeafChildValues);
    };

    private getAllItemsForCheckAll(
        triggerItem: BTSelectItem<ExtraDataType>,
        searchValue: string,
    ): (string | number)[] {
        const findParentGroup = (
            items: BTSelectItem<ExtraDataType>[],
            childValue: string | number,
        ): BTSelectItem<ExtraDataType> | undefined => {
            for (const group of items) {
                if (group.children?.some((child) => child.value === childValue)) {
                    return group;
                }
                if (group.children) {
                    const found = findParentGroup(group.children, childValue);
                    if (found) return found;
                }
            }
            return undefined;
        };

        const filteredLeafValues = this.filterToOnlyDisplayedChildren(triggerItem, searchValue);
        const resultSet = new Set<string | number>();
        const processedGroupIds = new Set<string>();

        filteredLeafValues.forEach((val) => {
            const item = this.findMatchingItem(this.btSelectItems, val);
            if (item) {
                if (item?.disableIndividualSelection) {
                    const parentGroup = findParentGroup(this.btSelectItems, val);
                    if (parentGroup && !processedGroupIds.has(parentGroup.id)) {
                        processedGroupIds.add(parentGroup.id);
                        this.getAllLeafChildValues(parentGroup).forEach((v) => resultSet.add(v));
                    }
                } else {
                    resultSet.add(val);
                }
            }
        });

        return Array.from(resultSet);
    }

    // NOTE: when ant adds an 'onClear' prop, we should look to add support for it and potentially replace our usage of onDropdownVisibleChange with it. https://github.com/ant-design/ant-design/issues/21498
    private handleDropdownVisibleChange = (open: boolean) => {
        this.canRemountSelect = false;

        // if the dropdown was open and is now closed, fire the close event listener
        if (open === false && this.state.open) {
            this.props.onBlur(this.props.id as string, true);
        }

        this.setState(
            {
                open,
            },
            () => {
                if (!open) {
                    this.canRemountSelect = true;
                }
            },
        );

        // bind scroll event
        if (this.inputGroup.current) {
            if (open) {
                addCloseOnScroll(this.inputGroup.current, this.handleScroll);
            } else {
                removeCloseOnScroll(this.inputGroup.current, this.handleScroll);
            }
        }
    };

    private handleScroll = (event: Event) => {
        // Scrolling the option list itself is the user browsing options, not a signal to dismiss.
        // The dropdown is portalled (getPopupContainer) rather than held in a ref, so match on
        // antd's dropdown class instead (ado-283026).
        if (
            event.target instanceof Element &&
            event.target.closest(".ant-select-dropdown") !== null
        ) {
            return;
        }
        // close on scroll
        this.handleDropdownVisibleChange(false);
    };

    private handleNativeBlur = () => {
        // Call onNativeBlur when the select loses focus (for components that need to track focus state)
        // This is separate from the onBlur prop which is called when dropdown closes
        if (this.props.onNativeBlur) {
            this.props.onNativeBlur();
        }
    };

    private getReadonlyDisplayItems = (
        selectItems: BTSelectItem<ExtraDataType>[] | undefined,
    ): BTSelectItem<ExtraDataType>[] => {
        const { value, ungroupedIds, displayDefaultItemReadonly } = this.props;
        const arr: BTSelectItem<ExtraDataType>[] = [];

        if (!selectItems) {
            return arr;
        }

        selectItems.forEach((selectItem) => {
            if (Array.isArray(value)) {
                if (value.includes(selectItem.value)) {
                    arr.push(selectItem);
                }
            } else if (
                value === selectItem.value &&
                (!ungroupedIds?.includes(selectItem.id) || displayDefaultItemReadonly)
            ) {
                arr.push(selectItem);
            }

            // go through all select item array children, and grab selected values
            if (selectItem.children) {
                arr.push(...this.getReadonlyDisplayItems(selectItem.children));
            }
        });
        return arr;
    };

    private renderOption = (option: BTSelectItem<ExtraDataType>) => {
        return (
            <Option
                key={option.key}
                value={option.value}
                disabled={option.disabled}
                className={option.className}
                title={option.title}
                label={this.props.customItemRender!(option)}
                data-searchvalue={this.props.filterOn!(option)}
                role="option"
            >
                {this.props.customItemRender!(option)}
            </Option>
        );
    };

    private renderGroup = (treeDataMember: BTSelectItem<ExtraDataType>): ReactNode => {
        const children = treeDataMember.children;

        if (isGroupParent(children)) {
            // Casting to string in case it comes in as a number from server
            const isDefaultOption =
                children.length === 1 &&
                this.props.ungroupedIds?.includes(children[0]?.id.toString());
            if (isDefaultOption) {
                return this.renderOption(children[0]);
            }

            return (
                <OptGroup label={treeDataMember.title} key={treeDataMember.key}>
                    {children.map((selectOption: any) => this.renderOption(selectOption))}
                </OptGroup>
            );
        }

        return this.renderOption(treeDataMember);

        function isGroupParent(
            c: BTSelectItem<ExtraDataType>[] | undefined,
        ): c is BTSelectItem<ExtraDataType>[] {
            return c !== undefined;
        }
    };

    private renderTreeNode = (option: BTSelectItem<ExtraDataType>) => {
        const combinedClassName = cn(option.className, {
            "bt-select-disable-individual-selection-node": option.disableIndividualSelection,
        });

        // For nodes with individual selection disabled, we still render them but with special styling to prevent interaction
        // This avoids whitespace issues with virtual scrolling
        return (
            <TreeNode
                key={option.key}
                disableCheckbox={option.disabled}
                disabled={option.disabled}
                value={option.value}
                title={this.props.customItemRender!(option)}
                searchValue={this.props.filterOn!(option)}
                isLeaf={!option.children || option.children.length === 0}
                icon={option.icon}
                className={combinedClassName}
            >
                {option.children && option.children.map(this.renderTreeNode)}
            </TreeNode>
        );
    };

    private getStyle(): React.CSSProperties | undefined {
        const { style } = this.props;
        // Width of the dropdown is always 100%
        if (style && style.width === undefined) {
            style.width = "100%";
        }
        return style || { width: "100%" };
    }

    private getInternalValue({ value, multiple }: BTSelectProps<FormValues, ExtraDataType>) {
        const newValue = Array.isArray(value) ? [...value] : value;

        if (multiple) {
            this.btSelectItems[0]?.children?.forEach((d) => {
                // Add parent if all children are selected & disabled
                if (Array.isArray(newValue) && d.children && d.disabled && d.selected) {
                    newValue.push(d.value);
                }
            });
        }

        return newValue;
    }

    private mapInternalSelectItemsFromTreeData(
        treeData: BTSelectItem<ExtraDataType>[],
        disabledIds: (string | number)[] | undefined,
        value: any,
    ) {
        function mapInternalSelectItems(
            item: BTSelectItem<ExtraDataType>,
            disabledIds: (string | number)[] | undefined,
            mappedSelectedItems: { [key: string]: boolean },
        ) {
            const result: BTSelectItem<ExtraDataType> = {
                ...item,
                disabled: disabledIds?.includes(item.id) ?? false,
                selected: mappedSelectedItems[item.value] === true,
            };

            const isParentItem = result.children && result.children.length > 0;
            if (isParentItem) {
                const newChildren: typeof result.children = [];
                const allChildren = { areDisabled: true, areSelected: true };
                result.children?.forEach((c) => {
                    const mappedChild = mapInternalSelectItems(c, disabledIds, mappedSelectedItems);
                    allChildren.areDisabled = allChildren.areDisabled && mappedChild.disabled;
                    allChildren.areSelected = allChildren.areSelected && mappedChild.selected;
                    newChildren.push(mappedChild);
                });

                result.children = newChildren;
                // disable when all children are disabled
                result.disabled = allChildren.areDisabled;
                // select when all children are selected
                result.selected = allChildren.areSelected;
            }

            return result;
        }
        const mappedSelectedItems: { [key: string]: boolean } = {};
        if (Array.isArray(value)) {
            value.forEach((x) => (mappedSelectedItems[x.value] = true));
        }
        return treeData.map((d) => mapInternalSelectItems(d, disabledIds, mappedSelectedItems));
    }

    private handleFilterOptionSingleSelect = (
        value: string,
        { disabled, "data-searchvalue": searchValue }: IAntSelectNode,
    ): boolean => this.isNodeMatch({ disabled, searchValue }, value);

    private handleFilterTreeNode = (
        search: string,
        node: IAntTreeSelectNode | undefined,
    ): boolean => {
        if (!node) {
            return false;
        }

        if (!this.props.onlySearchChildNodes && this.isNodeMatch(node, search)) {
            return true; // parent matches, keep parent + children
        }

        if (node.children && node.children.length > 0) {
            const childMatch = node.children.some((childNode) => {
                // check if child has children and recursively check for match
                if (childNode.children && childNode.children.length > 0) {
                    return this.handleFilterTreeNode(search, childNode);
                }
                return this.isNodeMatch(childNode, search);
            });

            // filter out if has children and none match
            if (!childMatch) {
                if (this.props.onlySearchChildNodes) {
                    return false;
                } else {
                    return this.isNodeMatch(node, search);
                }
            }
        } else {
            return this.isNodeMatch(node, search);
        }

        return false;
    };

    private isNodeMatch(
        node: Pick<IAntTreeSelectNode, "disabled" | "searchValue" | "title">,
        search: string,
    ) {
        const searchValue = node.searchValue ?? node.title?.toString();

        return (
            (!node.disabled && searchValue?.toLowerCase().includes(search.toLowerCase())) ?? false
        );
    }

    private handleDropdownRender = (menu: ReactElement) => {
        const { dropdownRender, "data-testid": testid } = this.props;
        return (
            <BTSelectCustomItemContext.Provider value={this.btSelectCustomItemContextDropdownValue}>
                <div data-testid={`${testid}-popup`}>
                    {dropdownRender ? dropdownRender(menu) : menu}
                </div>
            </BTSelectCustomItemContext.Provider>
        );
    };

    render() {
        const {
            // Destructured to keep it out of `...restProps` (which is spread onto
            // the multi-select TreeSelect, which builds its nodes from children,
            // not treeData). `this.props.treeData` is used directly where needed.
            treeData: _treeData,
            id,
            onChange,
            onBlur,
            onFocus,
            onNativeBlur,
            readOnly,
            "data-testid": testid,
            multiple,
            value,
            noTreeDataPlaceholder,
            notVisiblePlaceholder,
            disabledIds,
            fieldRef,
            autoFocus,
            treeCheckable,
            keepEnabledWhenEmpty,
            className,
            dropdownClassName,
            maxHeight,
            hideSelectAll,
            addonAfter,
            ungroupedIds,
            displayDefaultItemReadonly,
            customItemRender,
            tagRender,
            filterMode,
            onSearch,
            showSearch,
            allowClear,
            showArrow,
            notFoundContent,
            optionLabelProp,
            getPopupContainer,
            filterOn,
            shouldFilterOptions,
            dropdownCanExpand,
            style: _style,
            onlySearchChildNodes,
            disableVirtualScroll,
            removeDisabledCheckboxes,
            ariaLabel,
            formMode,
            defaultValue,
            loading,
            bds,
            ...restProps
        } = this.props;

        // height of 8.5 tree nodes
        const treePopupHeight = 8.5 * STANDARD_OPTION_HEIGHT;
        const checkable = treeCheckable;

        /**
         * TODO Remove selectAlignOverride, treeSelectAlignOverride, and dropdownAlignOverride
         * this was removed after ant v4 upgrade but wasn't fixed as anticipated
         * a minor version upgrade should make this unnecessary in the future
         * In the mean time, we need to override and wrap these so that we can use the spread
         * operator to bypass typescript's prop checking
         * @see https://github.com/react-component/select/pull/461
         * @see https://github.com/react-component/select/pull/710
         */
        const dropdownAlignOverride = {
            overflow: {
                adjustX: 1,
                adjustY: 1,
            },
        };
        const selectAlignOverride = {
            dropdownAlign: dropdownAlignOverride,
        };
        const treeSelectAlignOverride = {
            dropdownPopupAlign: dropdownAlignOverride,
        };

        const isClickToEdit = formMode === "click-to-edit" && !this.state.open;

        // When `bds`, emit the BDS class set (mirroring the former BdsSelect) so
        // only BTSelect.bds.less matches; otherwise emit the legacy class set so
        // only BTSelect.less matches. The two share the same antd DOM, so the
        // class swap alone re-skins the control.
        const combinedClassName = bds
            ? cn(
                  {
                      "bds-select--single": !multiple,
                      "bds-select--multi": multiple,
                      treeCheckable: multiple && checkable,
                      treeNotCheckable: multiple && !checkable,
                      maxHeightXSmall: maxHeight === "xsmall",
                      maxHeightSmall: maxHeight === "small",
                      maxHeightMiddle: maxHeight === "middle",
                      maxHeightLarge: maxHeight === "large",
                  },
                  className,
                  "bds-select",
                  "bds-theme",
              )
            : cn(
                  {
                      "BTSelect-single": !multiple,
                      "BTSelect-multi": multiple,
                      treeCheckable: multiple && checkable,
                      treeNotCheckable: multiple && !checkable,
                      "BTSelect-click-to-edit": isClickToEdit,
                      maxHeightXSmall: maxHeight === "xsmall",
                      maxHeightSmall: maxHeight === "small",
                      maxHeightMiddle: maxHeight === "middle",
                      maxHeightLarge: maxHeight === "large",
                  },
                  className,
                  "BTSelect",
              );
        const combinedDropdownClassName = bds
            ? cn(
                  {
                      "bds-select-dropdown--single": !multiple,
                      "bds-select-dropdown--multi": multiple,
                      dropdownCanExpand: dropdownCanExpand,
                      treeCheckable: multiple && checkable,
                      treeNotCheckable: multiple && !checkable,
                      hideSelectAll: multiple && hideSelectAll,
                  },
                  dropdownClassName,
                  "bds-theme",
              )
            : cn(
                  {
                      "BTSelect-dropdown-single": !multiple,
                      "BTSelect-dropdown-multi": multiple,
                      dropdownCanExpand: dropdownCanExpand,
                      treeCheckable: multiple && checkable,
                      treeNotCheckable: multiple && !checkable,
                      hideSelectAll: multiple && hideSelectAll,
                      removeDisabledCheckboxes: removeDisabledCheckboxes,
                  },
                  dropdownClassName,
              );

        const internalValue = this.getInternalValue(this.props);

        const isSelectEmpty = this.props.treeData?.length === 0;
        const allItemsIds = multiple
            ? getAllIds(this.btSelectItems).map((id) => id.toString())
            : undefined;

        if (readOnly) {
            const selectedBTSelectItems = this.getReadonlyDisplayItems(this.btSelectItems);
            if (multiple) {
                return (
                    <BTSelectCustomItemContext.Provider
                        value={this.btSelectCustomItemContextInputValue}
                    >
                        <ReadMore numberOfLines={2} lineHeight={2}>
                            <ValueDisplay
                                className={cn("BTSelect", { "bds-value-display": bds })}
                                data-testid={testid}
                                value={
                                    selectedBTSelectItems.length > 0 ? (
                                        <>
                                            {selectedBTSelectItems.map((d) => (
                                                <Fragment key={d.value}>
                                                    {customItemRender ===
                                                    defaultCustomItemRender ? (
                                                        <Tag
                                                            className={
                                                                bds ? "bds-tag-pill" : undefined
                                                            }
                                                        >
                                                            {customItemRender(d)}
                                                        </Tag>
                                                    ) : (
                                                        customItemRender!(d)
                                                    )}
                                                </Fragment>
                                            ))}
                                        </>
                                    ) : undefined
                                }
                            />
                        </ReadMore>
                    </BTSelectCustomItemContext.Provider>
                );
            }
            return (
                <BTSelectCustomItemContext.Provider
                    value={this.btSelectCustomItemContextInputValue}
                >
                    <ValueDisplay
                        className={bds ? "bds-value-display" : undefined}
                        data-testid={testid}
                        value={
                            selectedBTSelectItems.length > 0 ? (
                                <Space>
                                    {selectedBTSelectItems.map((d) => (
                                        <Fragment key={d.value}>
                                            {customItemRender === defaultCustomItemRender ? (
                                                <Tag className={bds ? "bds-tag-pill" : undefined}>
                                                    {customItemRender(d)}
                                                </Tag>
                                            ) : (
                                                customItemRender!(d)
                                            )}
                                        </Fragment>
                                    ))}
                                </Space>
                            ) : undefined
                        }
                    />
                </BTSelectCustomItemContext.Provider>
            );
        }

        // this is really ugly, but if we pass value={undefined} to ant TreeSelect/Select, it won't use the
        // default because it sees that the key "value" exists on the props object, so we need this workaround
        // instead. Only passing the `value` key when controlled also lets an uncontrolled `defaultValue` be
        // honored instead of being overridden by value={undefined}.
        const valueObject = internalValue === undefined ? {} : { value: internalValue };

        let placeholder = isSelectEmpty ? noTreeDataPlaceholder : restProps.placeholder;
        if (notVisiblePlaceholder) {
            placeholder = notVisiblePlaceholder;
        }

        const isDisabled =
            restProps.disabled || loading || (isSelectEmpty && !keepEnabledWhenEmpty);
        const showCheckedStrategy = filterMode ? TreeSelect.SHOW_PARENT : TreeSelect.SHOW_CHILD;
        const style = this.getStyle();

        return (
            <BTSelectCustomItemContext.Provider value={this.btSelectCustomItemContextInputValue}>
                <div ref={this.inputGroup} className="BTSelect-input-group">
                    <Input.Group compact className={combinedClassName}>
                        {this.props.addonBefore ?? ""}
                        {!multiple && (
                            // eslint-disable-next-line react/forbid-elements
                            <Select
                                id={id as string}
                                showSearch={showSearch === undefined || showSearch} // Default to true even if undefined. Only make false if actually false
                                style={style}
                                optionFilterProp="title"
                                suffixIcon={
                                    bds ? (
                                        <IconCaretSmallDown style={{ pointerEvents: "none" }} />
                                    ) : (
                                        <BTIconCaretSmallDown style={{ pointerEvents: "none" }} />
                                    )
                                }
                                // BDS look shows a check on the selected option; the
                                // legacy look relies on CSS only (no icon).
                                menuItemSelectedIcon={bds ? <IconCheck /> : undefined}
                                filterOption={
                                    shouldFilterOptions
                                        ? (this.handleFilterOptionSingleSelect as any)
                                        : false
                                }
                                dropdownClassName={combinedDropdownClassName}
                                disabled={isDisabled}
                                onChange={this.handleSingleSelectChange}
                                onDropdownVisibleChange={this.handleDropdownVisibleChange}
                                onFocus={onFocus}
                                onBlur={this.handleNativeBlur}
                                open={this.state.open}
                                {...valueObject}
                                defaultValue={
                                    Array.isArray(defaultValue) ? undefined : defaultValue
                                }
                                placeholder={placeholder}
                                getPopupContainer={getPopupContainer}
                                data-testid={testid}
                                size={restProps.size}
                                ref={fieldRef}
                                onSearch={
                                    showSearch === undefined || showSearch
                                        ? this.handleSearch
                                        : undefined
                                }
                                aria-label={ariaLabel}
                                allowClear={allowClear}
                                showArrow={showArrow}
                                loading={loading}
                                notFoundContent={notFoundContent}
                                optionLabelProp={optionLabelProp}
                                dropdownRender={this.handleDropdownRender}
                                tagRender={tagRender}
                                autoFocus={autoFocus}
                                virtual={!disableVirtualScroll}
                                {...selectAlignOverride}
                            >
                                {this.renderedSelectItems}
                            </Select>
                        )}

                        {multiple && (
                            <TreeSelect<any>
                                style={style}
                                key={`${testid}${this.remountSelect}`}
                                dropdownClassName={combinedDropdownClassName}
                                id={id as string}
                                multiple={multiple}
                                {...valueObject}
                                {...restProps}
                                defaultValue={defaultValue}
                                treeDefaultExpandAll={true}
                                treeExpandedKeys={allItemsIds}
                                showCheckedStrategy={showCheckedStrategy}
                                treeCheckable={checkable}
                                disabled={isDisabled}
                                placeholder={placeholder}
                                showSearch
                                suffixIcon={
                                    bds ? (
                                        <IconCaretSmallDown style={{ pointerEvents: "none" }} />
                                    ) : (
                                        <BTIconCaretSmallDown style={{ pointerEvents: "none" }} />
                                    )
                                }
                                onChange={this.handleMultipleSelectChange}
                                onDropdownVisibleChange={this.handleDropdownVisibleChange}
                                onFocus={onFocus}
                                onBlur={this.handleNativeBlur}
                                open={this.state.open}
                                autoClearSearchValue={false}
                                treeNodeFilterProp="searchValue"
                                listHeight={treePopupHeight}
                                getPopupContainer={getPopupContainer}
                                data-testid={testid}
                                ref={fieldRef}
                                searchValue={this.state.searchValue}
                                onSearch={this.handleSearch}
                                filterTreeNode={
                                    shouldFilterOptions ? this.handleFilterTreeNode : false
                                }
                                allowClear={allowClear}
                                showArrow={showArrow}
                                loading={loading}
                                notFoundContent={notFoundContent}
                                dropdownRender={this.handleDropdownRender}
                                tagRender={tagRender}
                                autoFocus={autoFocus}
                                aria-label={ariaLabel}
                                {...treeSelectAlignOverride}
                            >
                                {this.renderedSelectItems}
                            </TreeSelect>
                        )}

                        {addonAfter ? addonAfter : ""}
                    </Input.Group>
                </div>
            </BTSelectCustomItemContext.Provider>
        );
    }
}

export const BTSelectInternal = track((props) => ({
    element: `${props.multiple ? "Multi" : "Single"} Select`,
    uniqueId: props["data-testid"],
}))(BTSelectInternalBase);

export const BTSelect: new <FormValues = undefined, ExtraDataType = undefined>(
    props: BTSelectProps<FormValues, ExtraDataType>,
) => PureComponent<BTSelectProps<FormValues, ExtraDataType>> = BTSelectInternal as any;
